// «لمين الريّس». A bento of six image tiles (one large, four small, one wide),
// then the honest «مش ليك لو» underneath. REFERENCE.md §12, spec section 7.
//
// The reference has nothing like «مش ليك لو». It was deliberate on the old page
// and stays here as a quiet list, even though it makes the section taller.
//
// Below 1024 the grid is a rail that advances on its own. useAutoRail switches
// itself off on desktop, where nothing overflows.

import { useRef } from "react";
import { ArrowLeft, X } from "lucide-react";
import SectionHead from "./SectionHead";
import { SECTORS } from "./copy";
import { Capture, Photo } from "./media";
import useAutoRail from "./motion/useAutoRail";
import useSectionProgress from "./motion/useSectionProgress";
import "./Sectors.css";

// How wide each photo is really drawn, in vw: a landscape photo that covers a tile
// that is close to square runs far past the tile's sides, and the crop's zoom
// comes on top. Tiles were soft on a 2x screen when `sizes` gave the tile's width.
// The phone stays at the unzoomed width, so a zoomed tile does not cost it the 4K file.
const sizesFor = (i, zoom = 1) => {
    const drawn = i === 0 ? 63 : i === 5 ? 80 : 30;
    return `(min-width: 1024px) ${Math.round(drawn * zoom)}vw, 174vw`;
};

// Always the landscape photo: each crop in SECTORS is set against it, and it has to
// hold on a phone's tall tile as well (rooftop's emblem stays out of frame the same way).
function TileImage({ tile, sizes }) {
    const style = { "--ld-focal": tile.focal, "--ld-zoom": tile.zoom };
    return tile.capture
        ? <Capture name={tile.capture} sizes={sizes} alt="" style={style} />
        : <Photo name={tile.photo} sizes={sizes} portrait={false} style={style} />;
}

export default function Sectors({ who }) {
    const gridRef = useRef(null);
    useSectionProgress(gridRef, { smooth: true });
    useAutoRail(gridRef);

    const notItems = who.notItems || [];

    return (
        <section className="ld-section ld-section--day ld-section--flush ld-sectors" id="who" aria-labelledby="ld-sectors-title">
            <div className="ld-container">
                <SectionHead id="ld-sectors-title" eyebrow={who.eyebrow} title={who.title} lead={SECTORS.lead} />
            </div>

            <ul className="ld-sectors__grid" ref={gridRef} data-ld="cards">
                {SECTORS.tiles.map((tile, i) => {
                    const Icon = tile.icon;
                    return (
                        <li className={`ld-sectors__item ld-sectors__item--${i}`} key={tile.id}>
                            <a className="ld-sector" href={tile.href}>
                                <div className={`ld-sector__media ${tile.capture ? "ld-sector__media--screen" : ""}`.trim()} aria-hidden="true">
                                    <TileImage tile={tile} sizes={sizesFor(i, tile.zoom)} />
                                </div>
                                <div className="ld-sector__caption">
                                    <span className="ld-disc ld-disc--sm ld-disc--on-day"><Icon aria-hidden="true" /></span>
                                    <h3 className="ld-sector__title">{tile.title}</h3>
                                    <ArrowLeft className="ld-sector__arrow" aria-hidden="true" />
                                </div>
                            </a>
                        </li>
                    );
                })}
            </ul>

            {notItems.length ? (
                <div className="ld-container">
                    <div className="ld-sectors__not" data-ld="rise">
                        <h3 className="ld-label ld-sectors__not-title">{who.notTitle}</h3>
                        <ul className="ld-small ld-sectors__not-list">
                            {notItems.map((line) => (
                                <li key={line}>
                                    <X className="ld-sectors__not-icon" aria-hidden="true" />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
